import React, { useRef } from "react";
import { FaFacebookF, FaYoutube, FaLinkedinIn, FaSearch, FaUser, FaFileUpload } from "react-icons/fa";
import "./App.css";
import { MdContactSupport } from "react-icons/md";

import * as XLSX from "xlsx"; // You’ll need this
import { db } from "./firebase"; // Your Firebase setup
import { collection, doc, setDoc, getDocs, deleteDoc } from "firebase/firestore";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackAnalyticsEvent } from "./analytics/siteAnalytics";

function TopBar() {
  const dropdownRef = useRef(null);
  const analyticsSearchTimerRef = useRef(null);
  const lastTrackedSearchRef = useRef("");

  // 👇 add: detect narrow screens for scrolling behavior
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

    const newSlugs = new Set();

    // Step 1: Build list of slugs from Excel
    const productDocs = [];
    for (const row of rows) {
      if (!row["Product (L3)"]) continue;

      const includeFlag = (row["Include Y or N"] || "").trim().toUpperCase();
      if (includeFlag === "N") continue;

      const slug = slugify(row["Product (L3)"] || "");
      newSlugs.add(slug);

      const docData = {
        productFamily: row["Product Family (L1)"],
        subFamily: row["Sub Family (L2)"] || "",
        product: row["Product (L3)"] || "",
        application: splitMulti(row["Application (use case)"] || ""),
        industry: splitMulti(row['Industry \n(refer to "Solution" choices)'] || ""),
        size: row["Size/length/Width/Height"] || "",
        density: row["density/capacity"] || "",
        cableTypes: splitMulti(row["Cable types"] || ""),
        connectorTypes: splitMulti(row["Connector Types"] || ""),
        color: splitMulti(row["Color"] || ""),
        certifications: splitMulti(row["Certifications"] || ""),
        beadBaba: (row["BEAD/BABA (Y/N)"] || "").trim().toUpperCase() === "Y",
        access: row["Trunk Cable Access (front/rear)"] || "",
        environment: row["Environment"] || "",
        integration: row["Optical Component Integration"] || "",
        mounting: row["Mounting Options"] || "",
        portDensity: row["Port/Fiber Density"] || "",
        fiberType: splitMulti(row["Fiber Type (Fiber Mode)"] || ""),
        spacing: row["Spacing"] || "",
        numChannels: row["Number of Channels"] || "",
        fiberCount: splitMulti(row["Fiber Count"] || ""),
        relatedProducts: splitMulti(row["Related Products (pull from Product L3 - Column C)"] || ""),
        headline: row["Headline"] || "",
        subhead: row["Subhead"] || "",
        text: row["Text (character limit)"] || "",
        section1Heading: row["Section 1 Heading"] || "",
        section1Description: row["Section 1 Description"] || "",
        section2Heading: row["Section 2 Heading"] || "",
        section2Description: row["Section 2 Description"] || "",
        section3Heading: row["Section 3 Heading"] || "",
        section3Description: row["Section 3 Description"] || "",

        image: row["Image"] || "",
        image2: row["Image2"] || "",
        image3: row["Image3"] || "",
        image4: row["Image4"] || "",
        image5: row["Image5"] || "",
        image6: row["Image6"] || "",
        image7: row["Image7"] || "",
        image8: row["Image8"] || "",
        image9: row["Image9"] || "",

        features: splitMultiSemiColon(row["Features"] || ""),
        features2: splitMultiSemiColon(row["Features2"] || ""),
        features3: splitMultiSemiColon(row["Features3"] || ""),

        featuresHeading: row["FeaturesHeading"] || "",
        features2Heading: row["Features2Heading"] || "",
        features3Heading: row["Features3Heading"] || "",

        faq: splitMultiSemiColon(row["FAQs"] || ""),

        dataSheet: row["Data sheet"] || "",
        installationGuide: row["Installation Guide"] || "",
        brochure: row["Brochure"] || "",
        featuredContentVideo: row["FeaturedContentVideo"] || "",
        installationVideo: row["InstallationVideo"] || "",
        installationVideos: row["InstallationVideos"] || "",
        introVideo: row["IntroVideo"] || "",
        productSheet: row["Product Sheet"] || "",
        quickInstallationGuide: row["Quick Installation Guide"] || "",
        specialVideo1: row["SpecialVideo1"] || "",
        specialVideo1Title: row["SpecialVideo1Title"] || "",
        specialVideo2: row["SpecialVideo2"] || "",
        specialVideo2Title: row["SpecialVideo2Title"] || "",

        industry: splitMulti(row["Industry (refer to Solution choices)"] || ""),
        rank: row["Rank"] || "",
        slug,
      };

      productDocs.push({ slug, docData });
    }

    // Step 2: Delete old products not in Excel
    const existingSnap = await getDocs(collection(db, "products"));
    for (const docSnap of existingSnap.docs) {
      const existingSlug = docSnap.id;
      if (!newSlugs.has(existingSlug)) {
        await deleteDoc(docSnap.ref);
      }
    }

    // Step 3: Write new/updated products
    for (const { slug, docData } of productDocs) {
      const docRef = doc(db, "products", slug);
      await setDoc(docRef, docData);
    }

    alert("Upload complete!");
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    clearTimeout(analyticsSearchTimerRef.current);

    const fetchResults = async () => {
      const trimmedQuery = searchQuery.trim();

      if (!trimmedQuery) {
        setSearchResults([]);
        lastTrackedSearchRef.current = "";
        return;
      }

      try {
        const snapshot = await getDocs(collection(db, "products"));

        if (cancelled) return;

        const results = [];

        const query = trimmedQuery.toLowerCase().trim();

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();

          const productName = (data.product || "").toLowerCase().trim();

          const normalizedProductName = productName
            .replace(/®/g, "")
            .replace(/lenses/g, "lens")
            .replace(/lense/g, "lens")
            .replace(/micro lenses/g, "microlens")
            .replace(/micro lens/g, "microlens");

          const normalizedQuery = query
            .replace(/lenses/g, "lens")
            .replace(/lense/g, "lens")
            .replace(/micro lenses/g, "microlens")
            .replace(/micro lens/g, "microlens");

          const words = normalizedProductName.split(/\s+/);

          const noBrandInitials = words
            .slice(1)
            .map((word) => word[0])
            .join("");

          if (normalizedProductName.includes(normalizedQuery) || noBrandInitials.includes(normalizedQuery)) {
            results.push({
              name: data.product,
              slug: data.slug || docSnap.id,
              image: data.image,
            });
          }
        });

        const visibleResults = results.slice(0, 12);

        setSearchResults(visibleResults);

        analyticsSearchTimerRef.current = setTimeout(() => {
          const analyticsTerm = trimmedQuery.toLowerCase();

          if (lastTrackedSearchRef.current === analyticsTerm) {
            return;
          }

          lastTrackedSearchRef.current = analyticsTerm;

          trackAnalyticsEvent("search", {
            searchTerm: trimmedQuery,
            resultCount: visibleResults.length,
            searchLocation: "top_bar",
          });

          if (visibleResults.length === 0) {
            trackAnalyticsEvent("search_zero_results", {
              searchTerm: trimmedQuery,
              resultCount: 0,
              searchLocation: "top_bar",
            });
          }
        }, 700);
      } catch (error) {
        console.error("Top bar search failed:", error);
      }
    };

    fetchResults();

    return () => {
      cancelled = true;

      clearTimeout(analyticsSearchTimerRef.current);
    };
  }, [searchQuery]);

  const splitMulti = (val) =>
    typeof val === "string"
      ? val
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
      : [];

  const splitMultiSemiColon = (val) =>
    typeof val === "string"
      ? val
          .split(";")
          .map((x) => x.trim())
          .filter(Boolean)
      : [];

  const slugify = (text) =>
    text
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]+/g, "")
      .replace(/\-\-+/g, "-");

  return (
    <div className="top-bar" style={{ paddingRight: "3vw", paddingLeft: "3vw" }}>
      <div className="top-left">
        <a
          href="https://www.youtube.com/@gofoton4899"
          target="_blank"
          rel="noopener noreferrer"
          className="top-bar-icon-link"
        >
          <FaYoutube size={22} />
        </a>

        <a
          href="https://www.linkedin.com/company/gofoton/"
          target="_blank"
          rel="noopener noreferrer"
          className="top-bar-icon-link"
        >
          <FaLinkedinIn size={22} />
        </a>
      </div>

      {/* CENTER ANNOUNCMENT BAR */}
      {/* <div className="top-center-announcement" aria-live="polite">
          <div className="ticker" role="status">
            <div className="ticker-track">
              <span>
                Visit us at OFC Booth <a target='_blank' href="https://ofc26.mapyourshow.com/8_0/exhview/index.cfm?orsearchtype0=exhibitor&orsearchvalue0=127&orsearchdisplay0=Exhibitor&orsearchvaluedisplay0=Go!Foton&hallID=C&selectedBooth=1638"  style={{color: 'white'}} className="announcement-link">#1638</a>
              </span>
              <span aria-hidden="true">
                &nbsp;&nbsp;•&nbsp;&nbsp;                Go!Foton Presents Optical Excellence for Smarter AI Infrastructure at OFC 2026  <a target='_blank' href="https://ofc26.mapyourshow.com/8_0/exhview/index.cfm?orsearchtype0=exhibitor&orsearchvalue0=127&orsearchdisplay0=Exhibitor&orsearchvaluedisplay0=Go!Foton&hallID=C&selectedBooth=1638"  style={{color: 'white'}} className="announcement-link">#1638</a>

              </span>
            </div>
          </div>
      </div> */}
      <div className="top-right">
        <div className="search-container top-bar-icon-link" ref={dropdownRef}>
          <FaSearch
            title="Search"
            size={18}
            onClick={() => {
              const nextOpen = !showSearch;

              setShowSearch(nextOpen);

              if (nextOpen) {
                trackAnalyticsEvent("search_open", {
                  searchLocation: "top_bar",
                });
              }
            }}
            style={{ cursor: "pointer" }}
          />
          {showSearch && (
            <div className="search-dropdown">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <div className="search-results">
                {searchResults.length > 0
                  ? searchResults.map((result, idx) => (
                      <div
                        key={idx}
                        className="search-result-item"
                        onClick={() => {
                          trackAnalyticsEvent("search_result_click", {
                            searchTerm: searchQuery.trim(),
                            productName: result.name || "",
                            productSlug: result.slug || "",
                            resultPosition: idx + 1,
                            searchLocation: "top_bar",
                          });

                          navigate(`/${result.slug}`);
                          setShowSearch(false);
                          setSearchQuery("");
                        }}
                      >
                        <img
                          src={result.image || "/images/placeholder.png"}
                          alt={result.name}
                          className="result-thumb"
                        />
                        <span className="result-name">{result.name}</span>
                      </div>
                    ))
                  : searchQuery.trim() && (
                      <div className="search-empty">
                        <p style={{ marginBottom: 12, color: "#4D4D4F" }}>Looking for a product?</p>
                        <button
                          className="cta-button"
                          onClick={() => {
                            trackAnalyticsEvent("search_view_all_click", {
                              searchTerm: searchQuery.trim(),
                              searchLocation: "top_bar",
                              reason: "zero_results",
                            });

                            navigate("/products");
                            setShowSearch(false);
                            setSearchQuery("");
                          }}
                        >
                          View All Products
                        </button>
                      </div>
                    )}
              </div>
            </div>
          )}
        </div>

        <a href="/contact-us" title="Contact Us" className="top-bar-icon-link">
          <MdContactSupport size={24} />
        </a>
        <a
          href="http://portal.gofoton.com/"
          title="Login"
          target="_blank"
          rel="noopener noreferrer"
          className="top-bar-icon-link"
        >
          <FaUser size={18} />
        </a>

        {/* <label htmlFor="upload" style={{ cursor: "pointer" }}>
          <FaFileUpload size={18} color="#D94929" />
          <input
            type="file"
            id="upload"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </label> */}
      </div>
    </div>
  );
}

export default TopBar;
